import React, { useState, useEffect, useRef } from "react";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../config/firebase";
import Icon from "./Icon";
import LoadingSpinner from "./LoadingSpinner";
import { useErrorLoggerWithAuth } from "../hooks/useErrorLoggerWithAuth";
import { useAuth } from "../contexts/AuthContext";
import devocionalInteracaoService from "../services/devocionalInteracaoService";
import Modal, { useModal } from "./Modal";

/** Data atual no timezone de São Paulo (YYYY-MM-DD) */
const getHojeSP = () =>
  new Date()
    .toLocaleString("pt-BR", {
      timeZone: "America/Sao_Paulo",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    })
    .split("/")
    .reverse()
    .join("-");

const DevocionalDiario = () => {
  const [devocional, setDevocional] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [audioError, setAudioError] = useState(false);
  const [totalCurtidas, setTotalCurtidas] = useState(0);
  const [totalVisualizacoes, setTotalVisualizacoes] = useState(0);
  const [visualizacoesIds, setVisualizacoesIds] = useState([]);
  const [curtidasIds, setCurtidasIds] = useState([]);
  const [mostrarVisualizacoes, setMostrarVisualizacoes] = useState(false);
  const [nomesVisualizacoes, setNomesVisualizacoes] = useState([]);
  const [loadingVisualizacoes, setLoadingVisualizacoes] = useState(false);
  const [audioProgress, setAudioProgress] = useState(0);
  const [audioDuration, setAudioDuration] = useState(0);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const audioRef = useRef(null);
  const contentRef = useRef(null);
  const { logError } = useErrorLoggerWithAuth();
  const { currentUser } = useAuth();
  const { showAlert, ModalComponents } = useModal();

  const curtido = curtidasIds.includes(currentUser?.uid);

  useEffect(() => {
    loadDevocional();
  }, []);

  useEffect(() => {
    if (!devocional || !currentUser?.uid) return;

    const hoje = getHojeSP();

    // Listener em tempo real para curtidas e visualizações
    const unsubscribe = devocionalInteracaoService.escutarInteracoes(hoje, (stats) => {
      setTotalVisualizacoes(stats.totalVisualizacoes);
      setTotalCurtidas(stats.totalCurtidas);
      setVisualizacoesIds(stats.visualizacoes);
      setCurtidasIds(stats.curtidas);
    });

    registrarVisualizacao();

    return () => unsubscribe();
  }, [devocional, currentUser?.uid]);

  useEffect(() => {
    if (contentRef.current) {
      setIsOverflowing(contentRef.current.scrollHeight > 200);
    }
  }, [devocional]);

  const loadDevocional = async () => {
    try {
      setLoading(true);
      setError(null);

      const hoje = getHojeSP();
      const docRef = doc(db, "devocionais", hoje);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        setDevocional(docSnap.data());
      } else {
        setError("Devocional do dia não disponível");
      }
    } catch (err) {
      console.error("[DevocionalDiario] Erro ao carregar:", err);
      setError("Erro ao carregar devocional");

      // Registrar erro
      await logError(err, {
        component: "DevocionalDiario",
        action: "loadDevocional",
        date: getHojeSP(),
      });
    } finally {
      setLoading(false);
    }
  };

  const registrarVisualizacao = async () => {
    try {
      if (!currentUser?.uid) return;

      const hoje = getHojeSP();
      await devocionalInteracaoService.registrarVisualizacao(hoje, currentUser.uid);
    } catch (err) {
      console.error("[DevocionalDiario] Erro ao registrar visualização:", err);
    }
  };

  const handleCurtir = async () => {
    try {
      if (!currentUser?.uid) {
        await showAlert({
          variant: "warning",
          title: "Login Necessário",
          message: "Você precisa estar logado para curtir o devocional.",
        });
        return;
      }

      const hoje = getHojeSP();
      const curtiu = await devocionalInteracaoService.curtirDevocional(
        hoje,
        currentUser.uid
      );

      // Atualização otimista — listener sincroniza o estado definitivo
      setCurtidasIds((prev) =>
        curtiu ? [...prev, currentUser.uid] : prev.filter((id) => id !== currentUser.uid)
      );
    } catch (err) {
      console.error("[DevocionalDiario] Erro ao curtir:", err);
      await logError(err, {
        component: "DevocionalDiario",
        action: "curtir",
      });
    }
  };

  const handleCompartilhar = async () => {
    try {
      // Montar o conteúdo completo do devocional
      const conteudoTexto = devocional.conteudo_paragrafos.join("\n\n");

      const textoCompartilhar = `Devocional do Dia — ${devocional.data}

${devocional.titulo}

Leitura: ${devocional.leitura_biblica}

"${devocional.versiculo_chave}"

${conteudoTexto}${devocional.frase_destaque ? `\n\n${devocional.frase_destaque}` : ""}${devocional.autor ? `\n\n— ${devocional.autor}` : ""}${devocional.audio_url ? `\n\nÁudio: ${devocional.audio_url}` : ""}

RTM Brasil`;

      // Verificar se a API de compartilhamento está disponível
      if (navigator.share) {
        await navigator.share({
          title: `Devocional - ${devocional.titulo}`,
          text: textoCompartilhar,
        });
      } else {
        // Fallback: copiar para área de transferência
        await navigator.clipboard.writeText(textoCompartilhar);
        await showAlert({
          variant: "success",
          title: "Copiado!",
          message:
            "O conteúdo completo do devocional foi copiado para sua área de transferência.",
        });
      }
    } catch (err) {
      if (err.name !== "AbortError") {
        // AbortError é quando usuário cancela o compartilhamento
        console.error("[DevocionalDiario] Erro ao compartilhar:", err);
        await showAlert({
          variant: "error",
          title: "Erro",
          message: "Não foi possível compartilhar o devocional.",
        });
      }
    }
  };

  const handleVerVisualizacoes = async () => {
    if (visualizacoesIds.length === 0) return;
    setMostrarVisualizacoes(true);
    setLoadingVisualizacoes(true);
    try {
      const nomes = await devocionalInteracaoService.buscarNomesUsuarios(visualizacoesIds);
      setNomesVisualizacoes(nomes);
    } catch (err) {
      console.error("[DevocionalDiario] Erro ao buscar nomes:", err);
      setNomesVisualizacoes([]);
    } finally {
      setLoadingVisualizacoes(false);
    }
  };

  const handleBaixarImagem = () => {
    if (devocional.image_url) {
      window.open(devocional.image_url, "_blank");
    }
  };

  const handleBaixarAudio = () => {
    if (devocional.audio_url) {
      window.open(devocional.audio_url, "_blank");
    }
  };

  const toggleAudio = () => {
    if (!audioRef.current) return;

    try {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play().catch(async (err) => {
          console.error("[DevocionalDiario] Erro ao reproduzir áudio:", err);
          setAudioError(true);

          // Registrar erro de reprodução de áudio
          await logError(err, {
            component: "DevocionalDiario",
            action: "playAudio",
            audioUrl: devocional?.audio_url,
            errorType: err.name,
          });
        });
      }
      setIsPlaying(!isPlaying);
    } catch (err) {
      console.error("[DevocionalDiario] Erro no toggleAudio:", err);
      setAudioError(true);

      logError(err, {
        component: "DevocionalDiario",
        action: "toggleAudio",
      });
    }
  };

  const handleAudioEnded = () => {
    setIsPlaying(false);
    setAudioProgress(0);
  };

  const handleAudioTimeUpdate = () => {
    if (audioRef.current) {
      setAudioProgress(audioRef.current.currentTime);
    }
  };

  const handleAudioLoadedMetadata = () => {
    if (audioRef.current) {
      setAudioDuration(audioRef.current.duration);
    }
  };

  const handleProgressChange = (e) => {
    const newTime = parseFloat(e.target.value);
    if (audioRef.current) {
      audioRef.current.currentTime = newTime;
      setAudioProgress(newTime);
    }
  };

  const formatTime = (seconds) => {
    if (isNaN(seconds)) return "0:00";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const handleImageError = async (e) => {
    console.error("[DevocionalDiario] Erro ao carregar imagem");
    setImageError(true);

    // Registrar erro de carregamento de imagem
    await logError(new Error("Falha ao carregar imagem do devocional"), {
      component: "DevocionalDiario",
      action: "loadImage",
      imageUrl: devocional?.image_url,
      errorType: "ImageLoadError",
    });
  };

  const handleAudioError = async (e) => {
    console.error("[DevocionalDiario] Erro ao carregar áudio");
    setAudioError(true);

    // Registrar erro de carregamento de áudio
    await logError(new Error("Falha ao carregar áudio do devocional"), {
      component: "DevocionalDiario",
      action: "loadAudio",
      audioUrl: devocional?.audio_url,
      errorType: "AudioLoadError",
    });
  };

  const toggleExpanded = () => setExpanded((prev) => !prev);

  if (loading) {
    return (
      <div className="card mb-4">
        <div className="card-body">
          <LoadingSpinner message="Carregando devocional..." size="md" />
        </div>
      </div>
    );
  }

  if (error || !devocional) {
    return (
      <div className="card mb-4">
        <div className="card-body text-center py-5">
          <Icon name="alert-circle" size={48} color="var(--color-warning)" />
          <p className="text-muted mt-3 mb-0">
            {error || "Devocional não disponível"}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="card mb-4">
      {/* 1. Header — ícone circular */}
      <div className="card-header d-flex align-items-center gap-3">
        <div className="devocional-header-icon">
          <Icon name="book-open" size={20} color="var(--color-primary)" />
        </div>
        <div>
          <h3 className="mb-1">Devocional do Dia</h3>
          <p className="text-sm text-muted mb-0">{devocional.data}</p>
        </div>
      </div>

      <div className="card-body">
        {/* 2. Imagem — sombra + zoom */}
        {devocional.image_url && (
          <div className="devocional-image-wrapper mb-3">
            {!imageError ? (
              <img
                src={devocional.image_url}
                alt={devocional.titulo}
                loading="lazy"
                onError={handleImageError}
              />
            ) : (
              <div className="text-center py-5">
                <Icon
                  name="alert-circle"
                  size={48}
                  color="var(--color-warning)"
                />
                <p className="text-muted mt-3 mb-0">
                  Não foi possível carregar a imagem
                </p>
              </div>
            )}
          </div>
        )}

        {/* 3. Título — borda lateral colorida */}
        <h2 className="devocional-title mb-3">{devocional.titulo}</h2>

        {/* 4. Barra de interação — pills coloridos */}
        <div className="devocional-interaction mb-3">
          <button
            onClick={handleCurtir}
            className="devocional-pill"
            title={curtido ? "Descurtir" : "Curtir"}
          >
            <span className="devocional-pill-icon devocional-pill-icon--like">
              <Icon
                name="heart"
                size={14}
                color={curtido ? "var(--color-error)" : "var(--color-text-muted)"}
                style={{ fill: curtido ? "var(--color-error)" : "none" }}
              />
            </span>
            <span>{totalCurtidas}</span>
          </button>

          <button
            onClick={handleVerVisualizacoes}
            className="devocional-pill"
            style={{ opacity: totalVisualizacoes > 0 ? 1 : 0.6 }}
            title={totalVisualizacoes > 0 ? "Ver quem visualizou" : "Nenhuma visualização"}
            disabled={totalVisualizacoes === 0}
          >
            <span className="devocional-pill-icon devocional-pill-icon--views">
              <Icon name="eye" size={14} color="var(--color-info)" />
            </span>
            <span>{totalVisualizacoes}</span>
          </button>

          <button
            onClick={handleCompartilhar}
            className="devocional-pill"
            title="Compartilhar devocional"
          >
            <span className="devocional-pill-icon devocional-pill-icon--share">
              <Icon name="share2" size={14} color="var(--color-success)" />
            </span>
            <span>Compartilhar</span>
          </button>
        </div>

        {/* 5. Downloads — chips coloridos */}
        {(devocional.image_url || devocional.audio_url) && (
          <div className="devocional-downloads mb-3">
            <span className="devocional-downloads-label">Downloads</span>
            {devocional.image_url && (
              <button onClick={handleBaixarImagem} className="devocional-chip">
                <Icon name="image" size={14} />
                Imagem
              </button>
            )}
            {devocional.audio_url && (
              <button onClick={handleBaixarAudio} className="devocional-chip">
                <Icon name="volume-on" size={14} />
                Áudio
              </button>
            )}
          </div>
        )}

        {/* 6. Leitura Bíblica — callout verde */}
        <div className="devocional-bible-callout mb-3">
          <div className="devocional-bible-callout-icon">
            <Icon name="book-open" size={16} color="var(--color-success)" />
          </div>
          <div>
            <div className="devocional-bible-callout-label">Leitura Bíblica</div>
            <div className="devocional-bible-callout-ref">{devocional.leitura_biblica}</div>
          </div>
        </div>

        {/* 7. Versículo Chave — quotation card */}
        <div className="devocional-verse-card mb-3">
          <p className="devocional-verse-card-text mb-0">
            {devocional.versiculo_chave}
          </p>
          <span className="devocional-verse-card-icon">
            <Icon name="message-circle" size={18} color="var(--color-primary)" />
          </span>
        </div>

        {/* 8. Audio Player — painel âmbar */}
        {devocional.audio_url && (
          <div className="devocional-audio-panel mb-4">
            {!audioError ? (
              <>
                <div className="devocional-audio-header">
                  <div className="devocional-audio-header-icon">
                    <Icon name="music" size={14} color="var(--color-warning-hover)" />
                  </div>
                  <span className="devocional-audio-header-label">Ouvir Devocional</span>
                </div>

                <div className="devocional-audio-body">
                  <button
                    onClick={toggleAudio}
                    className="devocional-audio-play"
                    title={isPlaying ? "Pausar áudio" : "Ouvir áudio"}
                  >
                    <Icon
                      name={isPlaying ? "volume-off" : "volume-on"}
                      size={20}
                      color="#fff"
                    />
                  </button>

                  <div className="devocional-audio-progress">
                    <input
                      type="range"
                      min="0"
                      max={audioDuration || 0}
                      value={audioProgress}
                      onChange={handleProgressChange}
                    />
                    <div className="devocional-audio-progress-times">
                      <span>{formatTime(audioProgress)}</span>
                      <span>{formatTime(audioDuration)}</span>
                    </div>
                  </div>
                </div>

                <audio
                  ref={audioRef}
                  src={devocional.audio_url}
                  onEnded={handleAudioEnded}
                  onError={handleAudioError}
                  onTimeUpdate={handleAudioTimeUpdate}
                  onLoadedMetadata={handleAudioLoadedMetadata}
                />
              </>
            ) : (
              <div className="text-center">
                <Icon name="alert-circle" size={32} color="var(--color-warning)" />
                <p className="text-muted mt-2 mb-0">
                  Não foi possível carregar o áudio
                </p>
              </div>
            )}
          </div>
        )}

        {/* Conteúdo */}
        <div
          ref={contentRef}
          className="mb-3"
          style={{
            maxHeight: expanded ? "none" : "200px",
            overflow: "hidden",
            position: "relative",
          }}
        >
          {devocional.conteudo_paragrafos.map((paragrafo, index) => (
            <p
              key={index}
              className="mb-3 text-muted"
              style={{ lineHeight: "1.6" }}
            >
              {paragrafo}
            </p>
          ))}
          {!expanded && isOverflowing && (
            <div
              style={{
                position: "absolute",
                bottom: 0,
                left: 0,
                right: 0,
                height: "80px",
                background:
                  "linear-gradient(transparent, var(--color-background))",
              }}
            />
          )}
        </div>

        {/* 9. Botão Ler Mais — pill colorido */}
        {isOverflowing && (
          <div className="text-center mb-3">
            <button onClick={toggleExpanded} className="devocional-expand-pill">
              <Icon name={expanded ? "chevron-up" : "chevron-down"} size={16} />
              <span>{expanded ? "Ler Menos" : "Ler Mais"}</span>
            </button>
          </div>
        )}

        {/* 10. Frase Destaque — ícone star + decorações */}
        {devocional.frase_destaque && (
          <div className="devocional-highlight mb-3">
            <div className="devocional-highlight-icon">
              <Icon name="star" size={18} color="#fff" />
            </div>
            <p className="devocional-highlight-text mb-0">
              {devocional.frase_destaque}
            </p>
          </div>
        )}

        {/* 11. Autor — ícone + estilo */}
        {devocional.autor && (
          <div className="devocional-autor mb-3">
            <Icon name="user" size={14} color="var(--color-text-muted)" />
            <span>— {devocional.autor}</span>
          </div>
        )}

        {/* 12. Footer — ícone decorativo */}
        <div className="devocional-footer mt-4 pt-3">
          <div className="devocional-footer-icon">
            <Icon name="heart" size={12} color="var(--color-text-muted)" />
          </div>
          <p className="text-sm text-muted mb-0">
            <strong>RTM Brasil.</strong>
            <br />
            Todos os direitos reservados.
          </p>
        </div>
      </div>

      {/* Modal de Visualizações */}
      <Modal
        isOpen={mostrarVisualizacoes}
        onClose={() => setMostrarVisualizacoes(false)}
        title={`Visualizações (${totalVisualizacoes})`}
        size="sm"
      >
        {loadingVisualizacoes ? (
          <div className="text-center py-4">
            <LoadingSpinner message="Carregando..." size="sm" />
          </div>
        ) : nomesVisualizacoes.length > 0 ? (
          <div className="devocional-views-list">
            {nomesVisualizacoes.map((user) => (
              <div key={user.uid} className="devocional-views-item">
                <div className="devocional-views-avatar">
                  <Icon name="user" size={14} color="var(--color-primary)" />
                </div>
                <span className="text-sm">{user.nome}</span>
                {curtidasIds.includes(user.uid) && (
                  <Icon name="heart" size={13} color="var(--color-error)" style={{ fill: "var(--color-error)", marginLeft: "auto", flexShrink: 0 }} />
                )}
              </div>
            ))}
          </div>
        ) : (
          <p className="text-muted text-center mb-0">Nenhuma visualização encontrada</p>
        )}
      </Modal>

      <ModalComponents />
    </div>
  );
};

export default DevocionalDiario;
